import React from 'react';
import ActionPhrase from './ActionPhrase';
import { type ActionPhrase as ActionPhraseType } from '@/types/actions';

interface ActionLineProps {
  phrases: ActionPhraseType[];
  rowIndex: number;
}

const ActionLine = ({ phrases, rowIndex }: ActionLineProps) => {
  if (!phrases || phrases.length === 0) {
    console.log('No phrases provided to ActionLine');
    return null;
  }

  // Set different speeds for each line, but slower overall
  const getDuration = (lineIndex: number) => {
    switch (lineIndex) {
      case 1: return 210; // 3 minutes and 30 seconds for first line (was 180)
      case 2: return 90;  // Keep middle line the same
      case 3: return 100; // Keep last line the same
      default: return 90;
    }
  };
  
  // Quadruple the phrases to ensure smooth infinite scroll
  const duplicatedPhrases = [...phrases, ...phrases, ...phrases, ...phrases];

  const getRowStyle = (index: number) => {
    switch (index) {
      case 1: return 'from-blue-500/10 to-blue-600/5';
      case 2: return 'from-purple-500/10 to-purple-600/5';
      case 3: return 'from-green-500/10 to-green-600/5';
      default: return 'from-primary/10 to-primary/5';
    }
  };

  return (
    <div className={`rounded-lg bg-gradient-to-r ${getRowStyle(rowIndex)} p-2 overflow-hidden`}>
      <div 
        className="relative w-full overflow-hidden"
        style={{
          maskImage: 'linear-gradient(to right, transparent, black 2%, black 98%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 2%, black 98%, transparent)'
        }}
      >
        <div 
          className="flex gap-6 animate-scroll whitespace-nowrap"
          style={{
            animationDuration: `${getDuration(rowIndex)}s`,
            animationDirection: 'normal', // All lines move left to right
            width: 'max-content'
          }}
        >
          {duplicatedPhrases.map((phrase, index) => (
            <div
              key={`${phrase.id}-${index}`}
              className="inline-flex shrink-0"
            >
              <ActionPhrase
                text={addEmojiToText(phrase.text)}
                categoryId={phrase.category_id}
                categorySlug={phrase.category_slug}
                rowIndex={rowIndex}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Enhanced emoji function with more emojis for better readability
const addEmojiToText = (text: string) => {
  const actionEmojis: { [key: string]: string } = {
    // Creative actions
    'create': '✨ ',
    'design': '🎨 ',
    'develop': '💻 ',
    'build': '🛠️ ',
    'craft': '🔨 ',
    'generate': '⚡ ',
    'compose': '🖋️ ',
    'write': '✍️ ',
    'draft': '📝 ',
    
    // Analytical actions
    'analyze': '📊 ',
    'research': '🔬 ',
    'explore': '🔍 ',
    'investigate': '🕵️ ',
    'examine': '🔎 ',
    'study': '📚 ',
    'review': '👁️ ',
    'assess': '⚖️ ',
    'evaluate': '📋 ',
    
    // Improvement actions
    'improve': '📈 ',
    'optimize': '⚡ ',
    'enhance': '✨ ',
    'upgrade': '⬆️ ',
    'refine': '💎 ',
    'polish': '✨ ',
    'streamline': '🔄 ',
    
    // Strategic actions
    'plan': '📅 ',
    'strategize': '🎯 ',
    'organize': '📂 ',
    'manage': '🗂️ ',
    'coordinate': '🤝 ',
    'launch': '🚀 ',
    
    // Transformative actions
    'transform': '🔄 ',
    'convert': '🔁 ',
    'change': '🔀 ',
    'adapt': '🦎 ',
    'modify': '🛠️ ',
    
    // Growth actions
    'grow': '🌱 ',
    'expand': '🌟 ',
    'scale': '📊 ',
    'increase': '📈 ',
    'amplify': '📢 ',
    
    // Learning actions
    'learn': '🧠 ',
    'discover': '💡 ',
    'understand': '🤔 ',
    'master': '🏆 ',
    
    // Technical actions
    'code': '💻 ',
    'program': '👨‍💻 ',
    'automate': '🤖 ',
    'test': '🧪 ',
    'debug': '🐛 ',
    'integrate': '🔄 ',
    'deploy': '🚀 ',
    
    // Communication actions
    'communicate': '💬 ',
    'present': '🎭 ',
    'share': '📤 ',
    'explain': '👨‍🏫 ',
    'teach': '👩‍🏫 ',
    
    // Innovation actions
    'innovate': '💡 ',
    'invent': '🔧 ',
    'pioneer': '🧭 ',
    'revolutionize': '💥 ',
    
    // Visualization actions
    'visualize': '👁️ ',
    'illustrate': '🖼️ ',
    'map': '🗺️ ',
    'diagram': '📊 ',
    'chart': '📈 ',
  };

  // Find the first word and remove any punctuation
  const firstWord = text.split(' ')[0].toLowerCase().replace(/[^\w\s]/gi, '');
  
  // Check if the first word is in our emoji mapping
  for (const [action, emoji] of Object.entries(actionEmojis)) {
    if (firstWord === action || firstWord.includes(action)) {
      return emoji + text;
    }
  }
  
  // If no specific match is found, use a default emoji based on first letter
  const firstLetter = firstWord.charAt(0).toLowerCase();
  const defaultEmojis: { [key: string]: string } = {
    'a': '🅰️ ', 'b': '🅱️ ', 'c': '©️ ', 'd': '🌀 ', 'e': '📧 ', 
    'f': '🏁 ', 'g': '🌐 ', 'h': '♓ ', 'i': 'ℹ️ ', 'j': '🗾 ', 
    'k': '🎋 ', 'l': '🕒 ', 'm': 'Ⓜ️ ', 'n': '🎵 ', 'o': '⭕ ', 
    'p': '🅿️ ', 'q': '🔍 ', 'r': '®️ ', 's': '💲 ', 't': '✝️ ', 
    'u': '⛎ ', 'v': '♈ ', 'w': '〰️ ', 'x': '❌ ', 'y': '💹 ', 'z': '⚡ '
  };
  
  return (defaultEmojis[firstLetter] || '▫️ ') + text;
};

export default ActionLine;
